/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Link as DSLink,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  DetailsHeader,
  EntitlementDetails,
  // SodDetailsSidePanel,
  ViolationsTabDetails,
} from '../../features';
import { useHandleMitigatingControl } from '../../features/Details/hooks';
import {
  AAG_SOD_ENTITLEMENT,
  AAG_SOD_VIOLATIONS,
  CF_NEU_STL,
  ENTITLEMENT_DEATILS_TABS,
  ENTITLEMENT_TAB_OPTIONS,
  TABS_FILTERS,
  VIOLATION_TAB_OPTIONS,
  VIOLATIONS_DETAILS_TABS,
  violationsEntitlement,
  violationsHome,
} from '../../shared/constants';
import {
  useGetQueryParams,
  useGetRouteParams,
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../shared/hooks';
import { detailsActions, globalActions } from '../../shared/store/actions';
import { testIds } from '../../tests';

import './Details.css';

/**
 * Details component that renders Link to SodMapped.
 *
 * @function
 * @returns {React.ReactElement} The rendered section containing the dummy components.
 *
 * TODO:
 * - Chip Status in header to be dynamic based on Violation status
 * - Mitigate Tab count needs to be dynamic
 * - Mitigating Control tab need to conditionally render based on the Violation status
 * - fix the SOD Header text truncation, it is broken on hover
 * - Sod Header for Ent need to have breadcrumbs above the title
 * - activeTab needs to be dynamic based on entry point, set on entrence, default by param source
 */
function Details(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { qId } = useGetRouteParams();
  const {
    type: source,
    title,
    user: userName,
    activeTab: activeTabParam,
    username,
  } = useGetQueryParams();
  const { updateActiveTab, navigateToUrl, navigateToBack } = useNavigateTo();

  const {
    user: { isMenuPinned },
    aag: {
      global: { dataTheme, isClassicExpAlertOpen },
      violationsTab: {
        controls: { tabInAction },
      },
    },
  } = useTypedSelector((state) => state);

  const detailsPageClasses = cx(
    'Sod-Details',
    isMenuPinned && 'Sod-Details--isMenuPinned'
  );

  const commonBodyWrapperClasses = 'Sod-Details-commonMainWrapper';

  useEffect(() => {
    batch(() => {
      dispatch(detailsActions.setQueryParam(qId));
      dispatch(detailsActions.setDetailsSouce(source));
    });
  }, [qId, source, dispatch]);

  // Tabs inside details page
  const activeTab = useMemo(() => {
    if (activeTabParam) {
      return activeTabParam;
    }

    if (source === violationsEntitlement) {
      return ENTITLEMENT_DEATILS_TABS.TAB_SUMMARY;
    }

    return VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS;
  }, [activeTabParam, source]);

  // For Badge count of mitigating control tab
  const { loadMitigateRow, mitigatingControlRows } =
    useHandleMitigatingControl();

  useEffect(() => {
    if (
      source === violationsHome &&
      qId &&
      tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value
    ) {
      const requestData = {
        id: parseInt(qId, 10),
        violationId: qId,
      };

      loadMitigateRow(requestData).catch(() => {});
    }
  }, [source, qId, loadMitigateRow, tabInAction]);

  // Details tab
  const tabOptions = useMemo(() => {
    if (source === violationsEntitlement) {
      return ENTITLEMENT_TAB_OPTIONS;
    }

    if (tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value) {
      return VIOLATION_TAB_OPTIONS.map((tab) => {
        if (
          tab.value === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING &&
          mitigatingControlRows?.length > 0
        ) {
          return {
            ...tab,
            badgeCount: mitigatingControlRows.length,
          };
        }

        return tab;
      });
    }

    if (tabInAction === TABS_FILTERS.CLOSEDCOUNT.value) {
      return VIOLATION_TAB_OPTIONS.filter(
        (tab) =>
          tab.value !== VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS &&
          tab.value !== VIOLATIONS_DETAILS_TABS.TAB_MITIGATING
      );
    }

    return VIOLATION_TAB_OPTIONS.filter(
      (tab) => tab.value !== VIOLATIONS_DETAILS_TABS.TAB_MITIGATING
    );
  }, [source, tabInAction, mitigatingControlRows]);

  const handleChange = useCallback(
    (value: string) => {
      updateActiveTab(value);
    },
    [updateActiveTab]
  );

  // Header params
  const headerParams = useMemo(() => {
    switch (source) {
      case violationsHome:
        return {
          title,
          description: (
            <Box
              tag='div'
              className='Sod-ViolationsTabDetails-headerParamDescriptionWrapper'>
              <Typography
                kind='body2'
                color={CF_NEU_STL}
                className='Sod-ViolationsTabDetails-headerParamViolationsBy'>
                {l('Violation by')}
              </Typography>
              <DSLink
                kind='button'
                text={`${userName}`}
                color='neutral'
                onClick={() => {
                  handleChange(VIOLATIONS_DETAILS_TABS.TAB_USER);
                }}
                className='Sod-ViolationsSidePanelBody-links'
              />
            </Box>
          ),
          tabInAction,
        };

      case violationsEntitlement:
        return {
          title,
          description: null,
          breadcrumbItems: [
            { text: 'Violations', href: AAG_SOD_VIOLATIONS },
            {
              text: 'Entitlements with Inherent Risk',
              href: AAG_SOD_ENTITLEMENT,
            },
          ],
        };

      default:
        return {
          title: l('Title'),
          description: l('Description'),
        };
    }
  }, [source, title, userName, tabInAction, handleChange, l]);

  const handleBackButton = useCallback(() => {
    batch(() => {
      dispatch(detailsActions.setQueryParam(undefined));
      dispatch(detailsActions.setDetailsSouce(null));
      dispatch(detailsActions.setSidePanelClose());

      // For conflict page controls
      dispatch(detailsActions.setViolationsTabActiveFilters([]));
      dispatch(detailsActions.setViolationsTabExpandKey(null));
    });

    if (source === violationsHome) {
      navigateToUrl(AAG_SOD_VIOLATIONS);

      return;
    }

    navigateToBack();
  }, [source, dispatch, navigateToBack, navigateToUrl]);

  // Details Body
  const getContent = useCallback(() => {
    switch (source) {
      case violationsHome:
        return (
          <ViolationsTabDetails
            activeTab={activeTab}
            handleChange={handleChange}
            className={commonBodyWrapperClasses}
            handleBackButton={handleBackButton}
            username={username}
          />
        );

      case violationsEntitlement:
        return (
          <EntitlementDetails
            activeTab={activeTab}
            className={commonBodyWrapperClasses}
          />
        );

      default:
        return <Loader kind='dots' format='inline' color='brand' />;
    }
  }, [activeTab, handleChange, source, handleBackButton, username]);

  return (
    <Box
      dataTheme={dataTheme}
      className={detailsPageClasses}
      dataTestId={testIds.SOD_DETAILS_PAGE}>
      {/* Header and Tabs */}
      <DetailsHeader
        params={headerParams}
        tabOptions={tabOptions.map((tab) => ({
          ...tab,
          label: l(tab.label),
        }))}
        activeTab={activeTab}
        setActiveTab={handleChange}
        handleBackButton={handleBackButton}
      />
      {/* Tab Content / Scroll Area */}
      <article className='Sod-Details-innerMainWrapper'>{getContent()}</article>
      <ModalIsland
        kind='question'
        alertTitle={l('Return to the Classic Experience?')}
        alertSubtitle={l(
          'You can switch back to the new experience at any time.'
        )}
        onClose={() => dispatch(globalActions.setIsClassisExperience(false))}
        isOpen={isClassicExpAlertOpen}
        primaryButton={
          <Button
            type='button'
            kind='filled'
            size='medium'
            onClick={() => {
              window.location.pathname = '/ECM/soddetail/sodmapped';
            }}>
            {l('Continue')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            size='medium'
            onClick={() => {
              dispatch(globalActions.setIsClassisExperience(false));
            }}>
            {l('Cancel')}
          </Button>
        }
      />
    </Box>
  );
}

export default Details;
